import { lazy } from "react";
import { BC_HQ, BC_REPORTS, PARTNERS } from "app_constants";

import { ICON_TYPE } from "components/icons/icon";
import { passPresentURLParams } from "helpers/routes";
import {
  DateRangeSupport,
  QueryParamDateFormat,
  formatForQueryParams,
} from "helpers/shared/formatters/dates";

import { LD_FEATURE_FLAGS } from "types/context";
import type { ISectionProps } from "types/navigation";

const SETTINGS: ISectionProps = {
  feature: "Settings",
  icon: ICON_TYPE.SETTINGS,
  path: "/settings",
  level: ["store", "brand"],
  dateSupport: DateRangeSupport.NONE,
  subSections: [
    {
      feature: "Point of Sale",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/location",
      legacyPath: () => "/settings",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Day Parts Reporting",
      app: PARTNERS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/day_parts",
      legacyPath: () => "/settings/location/day_parts",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Day Parts Configuration",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/day_parts_config",
      legacyPath: () => "/day_parts",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Void / Comp Reason",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/void_comp_reason",
      legacyPath: () => "/void_comp_reasons",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Tax",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/tax",
      legacyPath: () => "/tax_rates",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "UG Link",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/ug_link",
      legacyPath: () => "/merchant_profile/edit",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Tables",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/tables",
      legacyPath: () => "/tables",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Zones",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/zones",
      legacyPath: () => "/zones",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Labor Settings",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/labor_settings",
      legacyPath: () => "/overtime_setting",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Devices",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/devices",
      legacyPath: () => "/pos_devices",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Reader Configuration",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/emv",
      legacyPath: () => "/bluetooth_devices",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Printers",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/printers",
      legacyPath: () => "/printers",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Printer Groups",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/printer_groups",
      legacyPath: () => "/stations",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Tip Pooling",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/tip_pool",
      legacyPath: () => "/tip_pool",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Online Ordering",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/online_ordering",
      legacyPath: () => "/online_order_integrations",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Gift Cards",
      component: lazy(
        () =>
          import(
            "digital_gift_cards/components/DigitalGiftCardsSettingsPageContainer"
          )
      ),
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/settings/gift_cards",
      dateSupport: DateRangeSupport.NONE,
      requiresFeatureFlag: LD_FEATURE_FLAGS.digitalGiftCards,
      hidesContentToolbar: true,
    },
    {
      feature: "Subscriptions",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/subscriptions",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/subscription" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Tender Management",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/tenders",
      legacyPath: () => "/tenders",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Loyalty Configuration",
      app: PARTNERS,
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/settings/loyalty",
      legacyPath: () => "/marketing/loyalty_program/setup",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Integrations",
      app: BC_HQ,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/settings/integrations",
      legacyPath: ({ searchParams }) =>
        `/third_party_service_exports${passPresentURLParams(
          [
            "oauth2_success",
            "oauth2_error",
            "oauth2_error_description",
            "oauth2_error_uri",
          ],
          searchParams
        )}`,
      dateSupport: DateRangeSupport.NONE,
    },
  ],
};

export default SETTINGS;
